/** Whether at least one payment proof URL is stored on the order. */
export const hasOrderPaymentProofUploaded = (order) => {
  if (order?.has_payment_proof === true) return true
  if (order?.payment_proof_datetime) return true
  const urls = order?.meta?.payment_upload
  if (!Array.isArray(urls) || urls.length === 0) return false
  return urls.some((item) => {
    if (typeof item === 'string') return item.trim().length > 0
    const uri = item?.url || item?.fileUrl || ''
    return Boolean(uri)
  })
}

/** Payable amount after checkout credit is applied. */
export const getOrderPayableAmount = (order) =>
  Number(order?.payable_after_credit ?? order?.amount ?? 0)

/** True when no payment is owed (fully covered by credit note, etc.). */
export const isZeroPayableOrder = (order) => getOrderPayableAmount(order) <= 0

function toPaisa(value) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

function getVerifiedPaymentReceivedAmount(order) {
  const summary = order?.payment_proof_detail?.summary
  if (summary != null && Number.isFinite(Number(summary.received))) {
    return Number(summary.received)
  }
  const requests = order?.payment_proof_detail?.requests
  if (Array.isArray(requests)) {
    return requests
      .filter((r) => String(r.status || '').toUpperCase() === 'VERIFIED')
      .reduce(
        (sum, r) => sum + Number(r.received_amount ?? r.requested_amount ?? 0),
        0,
      )
  }
  if (
    order?.verified_payment_received != null &&
    Number.isFinite(Number(order.verified_payment_received))
  ) {
    return Number(order.verified_payment_received)
  }
  return 0
}

/**
 * Whether finance still has a proof request to verify.
 * true: a request is pending.
 * false: structured requests exist and none are pending.
 * null: only a legacy file is known, so callers keep the old proof-uploaded rule.
 */
function isPaymentProofAwaitingVerification(order) {
  const detail = order?.payment_proof_detail
  const summary = detail?.summary
  const requests = Array.isArray(detail?.requests) ? detail.requests : null
  const summaryPending = summary != null ? Number(summary.pending) : NaN
  const summaryReceived = summary != null ? Number(summary.received) : NaN
  const summaryKnown =
    (requests?.length ?? 0) > 0 ||
    (Number.isFinite(summaryPending) && summaryPending > 0) ||
    (Number.isFinite(summaryReceived) && summaryReceived > 0)

  if (summaryKnown) {
    if (Number.isFinite(summaryPending) && summaryPending > 0) return true
    if (
      requests?.some((r) => String(r.status || '').toUpperCase() === 'PENDING')
    ) {
      return true
    }
    return false
  }

  if (order?.has_pending_payment_proof === true) return true
  if (
    order?.has_payment_proof_request === true &&
    order?.has_pending_payment_proof === false
  ) {
    return false
  }
  return null
}

/**
 * Payment is in place for procurement / credit-note gating (file proof, SAP
 * existing balance auto-verified, or Paid status).
 */
export const isOrderPaymentSatisfiedForProcurement = (order) => {
  if (isZeroPayableOrder(order)) return true
  const payable = getOrderPayableAmount(order)
  if (payable <= 0) return true
  if (getVerifiedPaymentReceivedAmount(order) > 0) return true
  if (String(order?.payment_status || '').toUpperCase() === 'PAID') return true
  if (hasOrderPaymentProofUploaded(order)) return true
  return false
}

/** Filter / chip keys shared with OrdersListPage status dropdown. */
export const PAYMENT_STATUS_FILTER_UNPAID = 'UNPAID'
export const PAYMENT_STATUS_FILTER_AWAITING_VERIFICATION = 'AWAITING_VERIFICATION'
export const PAYMENT_STATUS_FILTER_EDITED_AWAITING_VERIFICATION =
  'EDITED_AWAITING_VERIFICATION'
export const PAYMENT_STATUS_FILTER_PAYMENT_REJECTED = 'PAYMENT_REJECTED'
export const PAYMENT_STATUS_FILTER_PARTIALLY_PAID = 'PARTIALLY_PAID'
export const PAYMENT_STATUS_FILTER_PAID = 'PAID'
export const PAYMENT_STATUS_FILTER_NOT_REQUIRED = 'NOT_REQUIRED'

export const PURCHASE_ORDER_PAYMENT_STATUS_FILTERS = [
  PAYMENT_STATUS_FILTER_UNPAID,
  PAYMENT_STATUS_FILTER_AWAITING_VERIFICATION,
  PAYMENT_STATUS_FILTER_EDITED_AWAITING_VERIFICATION,
  PAYMENT_STATUS_FILTER_PAYMENT_REJECTED,
  PAYMENT_STATUS_FILTER_PARTIALLY_PAID,
  PAYMENT_STATUS_FILTER_PAID,
  PAYMENT_STATUS_FILTER_NOT_REQUIRED,
]

export const PAYMENT_STATUS_FILTER_LABELS = {
  [PAYMENT_STATUS_FILTER_UNPAID]: 'Unpaid',
  [PAYMENT_STATUS_FILTER_AWAITING_VERIFICATION]: 'Awaiting Verification',
  [PAYMENT_STATUS_FILTER_EDITED_AWAITING_VERIFICATION]:
    'Edited — awaiting verification',
  [PAYMENT_STATUS_FILTER_PAYMENT_REJECTED]: 'Payment Rejected',
  [PAYMENT_STATUS_FILTER_PARTIALLY_PAID]: 'Partially Paid',
  [PAYMENT_STATUS_FILTER_PAID]: 'Paid',
  [PAYMENT_STATUS_FILTER_NOT_REQUIRED]: 'Not Required',
}

export const isPurchaseOrderPaymentStatusFilter = (value) =>
  PURCHASE_ORDER_PAYMENT_STATUS_FILTERS.includes(String(value || '').trim())

function getLatestPaymentProofRequest(order) {
  const requests = order?.payment_proof_detail?.requests
  if (!Array.isArray(requests) || !requests.length) return null
  return [...requests].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
  )[0]
}

/**
 * When the latest proof request is rejected (and nothing newer is pending),
 * finance/shop should see rejection — not "Awaiting Verification".
 */
export function getActivePaymentProofRejectionReason(order) {
  const latest = getLatestPaymentProofRequest(order)
  if (!latest) return ''
  if (String(latest.status || '').toUpperCase() !== 'REJECTED') return ''
  return String(
    latest.rejection_reason ||
      order?.payment_proof_detail?.summary?.latest_rejection_reason ||
      '',
  ).trim()
}

/**
 * Chip label/color for purchase-order payment status in the admin portal.
 * Aligns with mini SCRUM-707: Unpaid → Awaiting Verification after proof → Paid.
 * A verified request that does not cover the payable amount is Partially Paid,
 * not Awaiting Verification. After finance rejects the latest request, show
 * Payment Rejected (SCRUM-844 QA).
 */
export const getPaymentStatusChipProps = (order) => {
  const status = String(order?.payment_status || '')
  const upper = status.toUpperCase()

  if (upper === 'PAID') {
    return { label: 'Paid', color: 'success' }
  }

  if (upper === 'NOT REQUIRED') {
    return { label: 'Not Required', color: 'default' }
  }

  // Prefer explicit payable_after_credit so missing fields do not look like ৳0
  if (
    order?.payable_after_credit != null &&
    Number(order.payable_after_credit) <= 0
  ) {
    return { label: 'Not Required', color: 'default' }
  }

  if (order?.has_edited_pending_proof) {
    return { label: 'Edited — awaiting verification', color: 'info' }
  }

  const rejectionReason = getActivePaymentProofRejectionReason(order)
  const paymentProofRejected =
    order?.payment_proof_rejected === true || Boolean(rejectionReason)
  if ((upper === 'UNPAID' || upper === 'PENDING') && paymentProofRejected) {
    return { label: 'Payment Rejected', color: 'error' }
  }

  if (upper === 'UNPAID' || upper === 'PENDING') {
    const awaiting = isPaymentProofAwaitingVerification(order)
    if (awaiting === true || (awaiting == null && hasOrderPaymentProofUploaded(order))) {
      return { label: 'Awaiting Verification', color: 'warning' }
    }
    const receivedPaisa = toPaisa(getVerifiedPaymentReceivedAmount(order))
    const payablePaisa = toPaisa(getOrderPayableAmount(order))
    if (receivedPaisa > 0 && payablePaisa > receivedPaisa) {
      return { label: 'Partially Paid', color: 'info' }
    }
    if (receivedPaisa > 0 && payablePaisa > 0 && receivedPaisa >= payablePaisa) {
      return { label: 'Paid', color: 'success' }
    }
    return { label: 'Unpaid', color: 'error' }
  }

  if (!status) return null

  if (hasOrderPaymentProofUploaded(order)) {
    return { label: 'Awaiting Verification', color: 'warning' }
  }

  return { label: status, color: 'default' }
}
